import { Action } from '@/_core/fluxstore/types';
import { RichTextEditorState, CardLinkEntry, createCardLinkKey } from '../types';
import { useRichTextEditorModule } from '../useModule';
import { queryExecutor } from '@/_core/cqrs/queryExecutor';
import {
    PublishedStorymapLinkedExternalCardQuery,
    StorymapLinkedExternalCardQuery,
    StorymapLinkedExternalCardQueryResult,
} from '@/api/storymapCardDetails/queries';
import { LinkedCard, CardType } from '@/store/main/StorymapCardDetails/types';
import { ToolIntegrationToolType } from '@/pages/main/storymap/store/types';
import { useStorymapStore } from '@/pages/main/storymap/store';

const { registerMiddleware, registerReducer } = useRichTextEditorModule();

/**
 * Action to fetch external card link data directly via CQRS query.
 * This action is self-contained and does NOT depend on StorymapCardDetails store.
 */
export class FetchExternalCardLinkData extends Action<RichTextEditorState> {
    public static ACTION_TYPE = 'RichTextEditor::FetchExternalCardLinkData';
    public _res: StorymapLinkedExternalCardQueryResult | null = null;
    public _error: string | null = null;

    public constructor(
        public cardId: string,
        public storymapSlug: string
    ) {
        super(FetchExternalCardLinkData.ACTION_TYPE);
    }
}

const middleware = async (state: RichTextEditorState, action: FetchExternalCardLinkData): Promise<boolean> => {
    try {
        const storymapStore = useStorymapStore();
        const query = storymapStore.state.isPublishedRecipient
            ? new PublishedStorymapLinkedExternalCardQuery(
                storymapStore.state.publishedAccessKind,
                storymapStore.state.publishedAccessKey,
                action.storymapSlug,
                action.cardId
            )
            : new StorymapLinkedExternalCardQuery(action.cardId, action.storymapSlug);

        const res = await queryExecutor.executeQuery(
            query,
            (error) => {
                // If the card is not found or getting a bad response, we can assume it's deleted or doesn't exist
                return { isHandled: true, result: null };
            }
        );
        action._res = res;
    } catch (err: any) {
        action._error = err?.message || 'Unknown error fetching card data';
    }

    return true;
};

const reducer = (state: RichTextEditorState, action: FetchExternalCardLinkData): void => {
    const key = createCardLinkKey(action.cardId, action.storymapSlug);

    // Handle error case
    if (action._error) {
        state.cardLinksByKey[key] = {
            linkedCard: null,
            status: 'error',
            lastError: action._error,
        };
        return;
    }

    // Handle no data case (card not found / deleted)
    if (!action._res) {
        const trashedPlaceholder: LinkedCard = {
            id: action.cardId,
            title: '',
            storymapTitle: action.storymapSlug || '',
            color: 0,
            key: '',
            linkedItemKey: '',
            cardType: CardType.Unknown,
            isCardKeyEnabled: false,
            integrationToolType: ToolIntegrationToolType.NotIntegrated,
            isLinkedToIntegrationTool: false,
            isTrashed: true,
            isExternal: true,
        };
        state.cardLinksByKey[key] = {
            linkedCard: trashedPlaceholder,
            status: 'loaded',
        };
        return;
    }

    // Handle success case
    const linkedCard: LinkedCard = {
        id: action._res.Id,
        title: action._res.Title,
        storymapTitle: action._res.StorymapTitle,
        color: action._res.Color,
        key: action._res.Key,
        linkedItemKey: action._res.LinkedItemKey || '',
        cardType: action._res.IsActivity
            ? CardType.Activity
            : action._res.IsTask
            ? CardType.Task
            : action._res.IsSubtask
            ? CardType.Subtask
            : CardType.UnscheduledSubtask,
        isCardKeyEnabled: action._res.IsCardKeyEnabled,
        isTrashed: action._res.IsTrashed,
        integrationToolType: action._res.IntegrationToolType || ToolIntegrationToolType.NotIntegrated,
        isLinkedToIntegrationTool: action._res.IsLinkedToIntegrationTool || false,
        isExternal: true, // Always true for cards fetched via this action
    };

    state.cardLinksByKey[key] = {
        linkedCard,
        status: 'loaded',
    };
};

//@ts-ignore
registerMiddleware(FetchExternalCardLinkData.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(FetchExternalCardLinkData.ACTION_TYPE, reducer);
